import React, { useState, useEffect, useRef } from "react";
import { Dialog, Box, Typography, TextField, IconButton, Button, Paper } from "@mui/material";
import {
  Close,
  PlayArrow,
  Stop,
  Refresh,
  Download,
  CheckCircle,
  VerifiedUser,
  Minimize,
  Maximize,
  DragIndicator,
} from "@mui/icons-material";
import Draggable from "react-draggable";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useWorkflowStreamingExecution } from "../../../hooks/useWorkflowStreamingExecution";
import { PHASES, PHASE_STATUS, exportExecutionLog } from "../../../utils/streamingEventParser";
import { useApp } from "../../../context/AppContext";
import { processText } from "../../../utils/chatUtils";
import C from "./colors";
import terminalMarkdownComponents from "./terminalMarkdown";
import useConfirm from "../../ui/useConfirm";

const markdownComponents = terminalMarkdownComponents;

const PHASE_NAMES = {
  1: "Query Decomp",
  2: "Corpus Search",
  3: "Reranking",
  4: "Comprehensive",
  5: "Exec Summary",
  6: "Web Validation",
  7: "Task Gen",
};

/* ── Build flat terminal lines ── */
function buildLines(phaseState, analysisText, executiveSummaryText, getPhaseData) {
  const lines = [];
  Object.entries(PHASES).forEach(([sid, phase]) => {
    const id = parseFloat(sid);
    const data = phaseState?.[id] || {};
    const status = data.status || PHASE_STATUS.PENDING;
    if (status === PHASE_STATUS.PENDING) {
      return;
    }

    lines.push({ id: `h${id}`, kind: "header", text: `▸ ${phase.name}` });

    const pd = getPhaseData(id)?.data || {};
    if (id === 1) {
      (pd.generated_queries || []).forEach((q, i) =>
        lines.push({ id: `q${i}`, kind: "row", text: `  q${i + 1}  ${q}` })
      );
    }
    if (id === 2 && pd.query) {
      lines.push({ id: "sq", kind: "dim", text: `  ${pd.query}` });
      lines.push({
        id: "sr",
        kind: "row",
        text: `  master:${pd.master_results || 0}  private:${pd.private_results || 0}  total:${pd.total_results || 0}`,
      });
    }
    if (id === 3) {
      (pd.reranked_documents || []).slice(0, 8).forEach((doc, i) => {
        const sc = (doc.score || 0).toFixed(3);
        const t = (doc.title || doc.content || "Document").substring(0, 68);
        lines.push({ id: `d${i}`, kind: i < 3 ? "row" : "dim", text: `  [${sc}]  ${t}` });
      });
    }
    if (id === 4) {
      // Show topic decomposition details if available
      if (pd.topics) {
        lines.push({
          id: "p4_topic_mode",
          kind: "dim",
          text: `  Execution Mode: Topic Decomposed (${pd.topicCount || 0} topics)`,
        });
        Object.entries(pd.topics).forEach(([topicId, topic]) => {
          let statusStr = topic.status;
          if (topic.status === "complete") {
            const scoreStr =
              topic.judgeScore !== undefined ? `, score=${topic.judgeScore.toFixed(1)}` : "";
            const retries = topic.retryCount > 0 ? ` [retry x${topic.retryCount}]` : "";
            statusStr = `✓ complete (${topic.chars || 0} chars${scoreStr})${retries}`;
          } else if (topic.status === "running") {
            statusStr = `◷ running`;
          } else if (topic.status === "retrying") {
            statusStr = `⚠ retrying (score=${topic.judgeScore ? topic.judgeScore.toFixed(1) : "n/a"})`;
          } else {
            statusStr = `◷ pending`;
          }
          lines.push({
            id: `topic_${topicId}`,
            kind: topic.status === "complete" ? "row" : "dim",
            text: `    ▸ ${topic.label}: ${statusStr}`,
          });

          // If retrying, show the gaps
          if (topic.status === "retrying" && topic.gaps && topic.gaps.length > 0) {
            topic.gaps.forEach((gap, gIdx) => {
              lines.push({
                id: `topic_${topicId}_gap_${gIdx}`,
                kind: "dim",
                text: `      - Gap: ${gap}`,
              });
            });
          }
        });
      } else if (pd.agents) {
        lines.push({ id: "p4_agent_mode", kind: "dim", text: `  Execution Mode: Parallel Agents` });
        Object.entries(pd.agents).forEach(([agentId, agent]) => {
          let statusStr = agent.status;
          if (agent.status === "complete") {
            statusStr = `✓ complete (${agent.chars || 0} chars)`;
          } else if (agent.status === "running") {
            statusStr = `◷ running`;
          } else {
            statusStr = `◷ pending`;
          }
          lines.push({
            id: `agent_${agentId}`,
            kind: agent.status === "complete" ? "row" : "dim",
            text: `    ▸ ${agent.label}: ${statusStr}`,
          });
        });
      }

      // Show completeness validation score if available
      if (pd.completenessScore !== undefined) {
        lines.push({
          id: "p4_validation",
          kind: "row",
          text: `  Completeness Gate: ${(pd.completenessScore * 100).toFixed(1)}% (Threshold: ${(pd.validationThreshold || 0.85) * 100}%)`,
        });
        if (pd.dimensionScores && Object.keys(pd.dimensionScores).length > 0) {
          Object.entries(pd.dimensionScores).forEach(([dim, score]) => {
            lines.push({
              id: `p4_val_dim_${dim}`,
              kind: "dim",
              text: `    - ${dim}: ${(score * 100).toFixed(0)}%`,
            });
          });
        }
      }

      // Add a spacer or separator if we have agents/topics status lines
      if (pd.topics || pd.agents || pd.completenessScore !== undefined) {
        lines.push({ id: "p4_agent_separator", kind: "spacer" });
      }

      // Always add both CertGPT Answer and Executive Summary if available
      if (analysisText || executiveSummaryText) {
        lines.push({
          id: "phase4_content",
          kind: "phase4-content",
          analysisText: analysisText || "",
          executiveSummaryText: executiveSummaryText || "",
        });
      }
    }
    if (id === 6) {
      const results = pd.search_results || [];
      if (results.length > 0) {
        lines.push({
          id: "wh",
          kind: "table-header",
          text: `  ${"#".padEnd(3)} ${"Title".padEnd(60)} URL`,
        });
        lines.push({
          id: "wd",
          kind: "dim",
          text: `  ${"-".repeat(3)} ${"-".repeat(60)} ${"-".repeat(60)}`,
        });
        results.forEach((r, i) => {
          const num = `${i + 1}.`.padEnd(3);
          const title = (r.title || `Source ${i + 1}`).substring(0, 58).padEnd(60);
          const url = (r.url || r.domain || "").substring(0, 58);
          lines.push({ id: `w${i}`, kind: "row", text: `  ${num} ${title} ${url}` });
        });
      }
    }
    if (id === 7) {
      // Theme extraction - show ALL themes
      if (pd.count || (pd.themes && pd.themes.length > 0)) {
        const totalCount = pd.total_themes || pd.count || pd.themes.length;
        lines.push({ id: "te", kind: "row", text: `  themes extracted: ${totalCount}` });

        // Show ALL themes (not just preview)
        const allThemes = pd.themes || [];
        allThemes.forEach((theme, i) =>
          lines.push({ id: `th${i}`, kind: "dim", text: `    ${i + 1}  ${theme.title || theme}` })
        );
      }

      // Targeted search progress
      if (pd.theme && pd.theme_index) {
        const docs = pd.documents_found || 0;
        lines.push({
          id: "ts",
          kind: "row",
          text: `  search[${pd.theme_index}]: ${pd.theme}  docs:${docs}`,
        });
      }

      // Complexity analysis
      if (pd.complexity_score !== undefined) {
        const needs = pd.needs_subtasks ? "generating subtasks" : "complexity too low";
        lines.push({
          id: "ca",
          kind: "dim",
          text: `  complexity: ${pd.complexity_score}/5  →  ${needs}`,
        });
      }

      // Subtask generation progress
      if (pd.subtasks && pd.count) {
        lines.push({ id: "sg", kind: "row", text: `  subtasks[${pd.theme}]: ${pd.count}` });
      }

      // Final parent tasks (MULTIPLE) and child tasks
      if (pd.parent_tasks && pd.parent_tasks.length > 0) {
        lines.push({
          id: "summary",
          kind: "row",
          text: `  generated: ${pd.total_parents || pd.parent_tasks.length} parents, ${pd.total_children || 0} children`,
        });

        pd.parent_tasks.forEach((parentObj, pIdx) => {
          const parent = parentObj.parent_task;
          const children = parentObj.child_tasks || [];

          // Parent task header
          lines.push({
            id: `tp${pIdx}`,
            kind: "strong",
            text: `  [${pIdx + 1}] ${parent.title || parent.description || "Parent Task"}`,
          });

          // Child tasks
          children.forEach((child, cIdx) => {
            lines.push({
              id: `tc${pIdx}_${cIdx}`,
              kind: "dim",
              text: `    ${String(cIdx + 1).padStart(2, "0")}  ${child.title || child.description || `Task ${cIdx + 1}`}`,
            });
          });
        });
      }
    }
    // Display message (can be multi-line for stopped workflows)
    if (data.message) {
      data.message.split("\n").forEach((line, i) => {
        if (line.trim()) {
          lines.push({
            id: `m${id}_${i}`,
            kind: status === PHASE_STATUS.ERROR ? "error" : "dim",
            text: `  ${line}`,
          });
        }
      });
    }
    if (status === PHASE_STATUS.COMPLETE) {
      lines.push({ id: `ok${id}`, kind: "done", text: `  ✓ done` });
    }
    if (status === PHASE_STATUS.ERROR) {
      lines.push({ id: `stopped${id}`, kind: "error", text: `  ✗ stopped` });
    }
    lines.push({ id: `sp${id}`, kind: "spacer" });
  });
  return lines;
}

/* ── Terminal line ── */
const Line = React.memo(({ item, isLast, streaming }) => {
  const [activeView, setActiveView] = useState("certgpt"); // "certgpt" or "summary"

  if (item.kind === "spacer") {
    return <Box sx={{ height: 8 }} />;
  }
  if (item.kind === "header") {
    return (
      <Typography
        sx={{
          fontSize: "10.5px",
          fontWeight: 700,
          color: C.indigo,
          fontFamily: C.mono,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          mt: 0.5,
          mb: 0.25,
          lineHeight: "18px",
        }}
      >
        {item.text}
      </Typography>
    );
  }

  // Render Phase 4 content with button controls
  if (item.kind === "phase4-content") {
    const { analysisText, executiveSummaryText } = item;
    const hasBoth = analysisText && executiveSummaryText;

    return (
      <Box sx={{ pl: 2, pr: 1 }}>
        {/* Button Controls - only show if both are available */}
        {hasBoth && (
          <Box sx={{ mb: 2, display: "flex", gap: 1.5 }}>
            <Button
              onClick={() => setActiveView("certgpt")}
              size="small"
              sx={{
                textTransform: "none",
                fontSize: "11.5px",
                fontWeight: activeView === "certgpt" ? 700 : 600,
                color: activeView === "certgpt" ? "#fff" : C.sub,
                borderRadius: "6px",
                px: 2,
                py: 0.7,
                background:
                  activeView === "certgpt"
                    ? `linear-gradient(135deg, #818cf8, ${C.indigo})`
                    : "transparent",
                border: activeView === "certgpt" ? "none" : `1px solid ${C.border}`,
                boxShadow: activeView === "certgpt" ? "0 2px 8px rgba(86,100,210,0.3)" : "none",
                "&:hover": {
                  bgcolor: activeView === "certgpt" ? undefined : C.surface,
                  boxShadow: activeView === "certgpt" ? "0 4px 14px rgba(86,100,210,0.45)" : "none",
                },
              }}
            >
              CertGPT Answer
            </Button>
            <Button
              onClick={() => setActiveView("summary")}
              size="small"
              sx={{
                textTransform: "none",
                fontSize: "11.5px",
                fontWeight: activeView === "summary" ? 700 : 600,
                color: activeView === "summary" ? "#fff" : C.sub,
                borderRadius: "6px",
                px: 2,
                py: 0.7,
                background:
                  activeView === "summary"
                    ? `linear-gradient(135deg, ${C.amber}, #f59e0b)`
                    : "transparent",
                border: activeView === "summary" ? "none" : `1px solid ${C.border}`,
                boxShadow: activeView === "summary" ? "0 2px 8px rgba(251,191,36,0.3)" : "none",
                "&:hover": {
                  bgcolor: activeView === "summary" ? undefined : C.surface,
                  boxShadow: activeView === "summary" ? "0 4px 14px rgba(251,191,36,0.45)" : "none",
                },
              }}
            >
              Executive Summary
            </Button>
          </Box>
        )}

        {/* Render CertGPT Answer (Comprehensive Analysis) */}
        {activeView === "certgpt" && analysisText && (
          <>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {processText(analysisText)}
            </ReactMarkdown>
            {isLast && streaming && (
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: 6,
                  height: 12,
                  bgcolor: C.indigo,
                  ml: "2px",
                  verticalAlign: "text-bottom",
                  opacity: 0.7,
                  animation: "bl 1s step-end infinite",
                  "@keyframes bl": { "0%,100%": { opacity: 0.7 }, "50%": { opacity: 0 } },
                }}
              />
            )}
          </>
        )}

        {/* Render Executive Summary */}
        {activeView === "summary" && executiveSummaryText && (
          <>
            {/* Visual indicator - Executive Summary Header */}
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: "8px",
                background: C.amberBg,
                border: `1px solid ${C.amberBdr}`,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "6px",
                  background: "rgba(251,191,36,0.15)",
                  border: `1px solid ${C.amberBdr}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: C.amber }}>
                  1
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: C.text,
                    fontFamily: C.mono,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Executive Summary (1-Page)
                </Typography>
                <Typography sx={{ fontSize: "10px", color: C.sub, fontFamily: C.mono }}>
                  Key actions and requirements from comprehensive analysis
                </Typography>
              </Box>
            </Box>
            {/* Render executive summary markdown */}
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {processText(executiveSummaryText)}
            </ReactMarkdown>
            {isLast && streaming && (
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: 6,
                  height: 12,
                  bgcolor: C.amber,
                  ml: "2px",
                  verticalAlign: "text-bottom",
                  opacity: 0.7,
                  animation: "bl 1s step-end infinite",
                  "@keyframes bl": { "0%,100%": { opacity: 0.7 }, "50%": { opacity: 0 } },
                }}
              />
            )}
          </>
        )}

        {/* Fallback: show whatever is available if only one exists */}
        {!hasBoth && analysisText && (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {processText(analysisText)}
          </ReactMarkdown>
        )}
        {!hasBoth && executiveSummaryText && !analysisText && (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {processText(executiveSummaryText)}
          </ReactMarkdown>
        )}
      </Box>
    );
  }

  const color =
    item.kind === "done"
      ? C.indigo
      : item.kind === "error"
        ? C.error // red for errors
        : item.kind === "dim"
          ? C.dim // #64748b — readable slate
          : item.kind === "strong"
            ? C.text // near-black
            : item.kind === "table-header"
              ? C.indigo // indigo for table headers
              : item.kind === "analysis"
                ? C.sub // dark slate for streaming text
                : C.sub; // default: dark slate
  const fontWeight = item.kind === "table-header" ? 700 : 400;
  return (
    <Typography
      sx={{
        fontSize: "11.5px",
        fontFamily: C.mono,
        color,
        fontWeight,
        lineHeight: "19px",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {item.text}
      {isLast && streaming && item.kind === "analysis" && (
        <Box
          component="span"
          sx={{
            display: "inline-block",
            width: 6,
            height: 12,
            bgcolor: C.indigo,
            ml: "2px",
            verticalAlign: "text-bottom",
            opacity: 0.7,
            animation: "bl 1s step-end infinite",
            "@keyframes bl": { "0%,100%": { opacity: 0.7 }, "50%": { opacity: 0 } },
          }}
        />
      )}
    </Typography>
  );
});

/* ── Phase timeline item ── */
const PhaseItem = ({ id, phaseData, isActive, isLast: last }) => {
  const status = phaseData?.status || PHASE_STATUS.PENDING;
  const isDone = status === PHASE_STATUS.COMPLETE;
  const isErr = status === PHASE_STATUS.ERROR;
  const dotColor = isErr ? C.error : isDone || isActive ? C.indigo : C.dim;

  return (
    <Box sx={{ display: "flex", gap: 1.25, minHeight: 36, alignItems: "flex-start" }}>
      {/* Timeline column */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: 16,
          flexShrink: 0,
          pt: "4px",
        }}
      >
        {/* Dot */}
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            flexShrink: 0,
            bgcolor: isActive ? C.indigo : isDone ? C.indigo : "transparent",
            border: `1.5px solid ${dotColor}`,
            transition: "all 0.25s",
            boxShadow: isActive ? `0 0 0 3px ${C.indigoBg}` : "none",
            ...(isActive && {
              animation: "pd 1.4s ease-in-out infinite",
              "@keyframes pd": {
                "0%,100%": { boxShadow: `0 0 0 3px ${C.indigoBg}` },
                "50%": { boxShadow: `0 0 0 5px ${C.indigoBg}` },
              },
            }),
          }}
        />
        {/* Connector line */}
        {!last && (
          <Box
            sx={{
              width: "1.5px",
              flex: 1,
              bgcolor: isDone ? C.indigoBdr : C.border,
              mt: "3px",
              minHeight: 18,
            }}
          />
        )}
      </Box>
      {/* Label */}
      <Box sx={{ pt: "2px", pb: 1 }}>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: isActive || isDone ? 600 : 400,
            color: isActive ? C.indigo : isDone ? C.text : C.dim,
            lineHeight: 1.3,
            transition: "color 0.2s",
          }}
        >
          {PHASE_NAMES[id]}
        </Typography>
        {isActive && (
          <Typography sx={{ fontSize: "9.5px", color: C.indigo, fontFamily: C.mono, opacity: 0.8 }}>
            running
          </Typography>
        )}
        {isDone && (
          <Typography sx={{ fontSize: "9.5px", color: C.sub, fontFamily: C.mono }}>done</Typography>
        )}
      </Box>
    </Box>
  );
};

/* ── Agent Coordination & Execution Dashboard ── */
const AgentExecutionDashboard = ({ pd, isStreaming }) => {
  const useTopicMode = !!pd.topics;
  const items = useTopicMode ? Object.values(pd.topics) : Object.values(pd.agents || {});

  if (items.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: `1px solid ${C.border}`,
        bgcolor: C.surface,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        flexShrink: 0,
      }}
    >
      {/* Title & Mode */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontSize: "10.5px",
              fontWeight: 700,
              color: C.indigo,
              fontFamily: C.mono,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {useTopicMode ? "Topic-Decomposed Execution" : "Parallel Agent Coordination"}
          </Typography>
          {isStreaming && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: C.indigo,
                animation: "pulse 1.5s ease-in-out infinite",
                "@keyframes pulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
              }}
            />
          )}
        </Box>
        {useTopicMode && (
          <Typography sx={{ fontSize: "10px", color: C.dim, fontFamily: C.mono }}>
            {items.filter((t) => t.status === "complete").length} / {items.length} Topics Complete
          </Typography>
        )}
      </Box>

      {/* Grid of Agent Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: 1.25,
        }}
      >
        {items.map((item) => {
          const status = item.status || "pending";
          const isComplete = status === "complete";
          const isRunning = status === "running";
          const isRetrying = status === "retrying";

          let cardBg = C.bg;
          let cardBdr = C.border;
          let statusColor = C.dim;

          if (isComplete) {
            cardBg = "rgba(16,185,129,0.02)";
            cardBdr = "rgba(16,185,129,0.2)";
            statusColor = "#10b981";
          } else if (isRunning) {
            cardBg = C.indigoBg;
            cardBdr = C.indigoBdr;
            statusColor = C.indigo;
          } else if (isRetrying) {
            cardBg = C.amberBg;
            cardBdr = C.amberBdr;
            statusColor = C.amber;
          }

          // Render judge score or status text
          let scoreBadge = null;
          if (useTopicMode && (item.judgeScore !== undefined || isRetrying)) {
            const scoreVal = item.judgeScore;
            const formattedScore = scoreVal !== undefined ? scoreVal.toFixed(1) : "n/a";
            const isPassing = scoreVal >= 7.0; // 7.0 threshold

            scoreBadge = (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 0.75,
                  py: 0.2,
                  borderRadius: "4px",
                  fontSize: "9px",
                  fontWeight: 600,
                  fontFamily: C.mono,
                  bgcolor: isPassing ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                  color: isPassing ? "#10b981" : "#ef4444",
                  border: `1px solid ${isPassing ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)"}`,
                }}
              >
                Score: {formattedScore}
              </Box>
            );
          }

          return (
            <Box
              key={item.id || item.label}
              sx={{
                p: 1.25,
                borderRadius: "8px",
                border: `1px solid ${cardBdr}`,
                bgcolor: cardBg,
                transition: "all 0.3s ease",
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Pulse line for active agents */}
              {(isRunning || isRetrying) && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "2px",
                    width: "100%",
                    bgcolor: isRunning ? C.indigo : C.amber,
                    animation: "shimmer 2s infinite linear",
                    "@keyframes shimmer": {
                      "0%": { transform: "translateX(-100%)" },
                      "100%": { transform: "translateX(100%)" },
                    },
                  }}
                />
              )}

              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: C.text,
                    fontFamily: C.mono,
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                    flex: 1,
                  }}
                >
                  {item.label}
                </Typography>

                {/* Status Dot */}
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: statusColor,
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                />
              </Box>

              {/* Info / Metadata row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 0.5,
                  mt: "auto",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "9.5px",
                    color: statusColor,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.02em",
                    fontFamily: C.mono,
                  }}
                >
                  {status}
                </Typography>

                {scoreBadge}
              </Box>

              {/* Extra details (retry count, characters, gaps) */}
              {(item.retryCount > 0 || item.chars > 0 || (item.gaps && item.gaps.length > 0)) && (
                <Box
                  sx={{
                    mt: 0.5,
                    pt: 0.75,
                    borderTop: `1px dashed ${C.border}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "9px",
                      color: C.dim,
                      fontFamily: C.mono,
                    }}
                  >
                    {item.retryCount > 0 && <span>Retries: {item.retryCount}</span>}
                    {item.chars > 0 && <span>Size: {item.chars} ch</span>}
                  </Box>

                  {isRetrying && item.gaps && item.gaps.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography
                        sx={{ fontSize: "8.5px", fontWeight: 600, color: C.error, mb: 0.25 }}
                      >
                        Gaps to address:
                      </Typography>
                      {item.gaps.slice(0, 2).map((gap, gIdx) => (
                        <Typography
                          key={gIdx}
                          sx={{
                            fontSize: "8px",
                            color: C.dim,
                            lineHeight: 1.2,
                            pl: 1,
                            borderLeft: `1px solid ${C.error}`,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={gap}
                        >
                          {gap}
                        </Typography>
                      ))}
                      {item.gaps.length > 2 && (
                        <Typography
                          sx={{ fontSize: "7.5px", color: C.muted, fontStyle: "italic", pl: 1 }}
                        >
                          + {item.gaps.length - 2} more...
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Assembly & Completeness Validation Gate Display */}
      {(pd.assemblyStatus || pd.validationStatus || pd.completenessScore !== undefined) && (
        <Box
          sx={{
            p: 1.25,
            borderRadius: "8px",
            border: `1px solid ${C.border}`,
            bgcolor: C.surface,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: C.sub,
                fontFamily: C.mono,
                textTransform: "uppercase",
              }}
            >
              Assembly &amp; Quality Gates
            </Typography>
            {pd.completenessScore !== undefined && (
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 700,
                  fontFamily: C.mono,
                  color: pd.isComplete ? "#10b981" : C.amber,
                }}
              >
                Completeness Gate: {(pd.completenessScore * 100).toFixed(0)}%
                {pd.validationThreshold && ` (Goal: ${(pd.validationThreshold * 100).toFixed(0)}%)`}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {/* Assembly status */}
            {pd.assemblyStatus && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography sx={{ fontSize: "9.5px", color: C.dim, fontFamily: C.mono }}>
                  Assembly:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "9.5px",
                    fontWeight: 600,
                    fontFamily: C.mono,
                    color: pd.assemblyStatus === "complete" ? "#10b981" : C.indigo,
                  }}
                >
                  {pd.assemblyStatus === "complete"
                    ? `✓ Complete (${pd.totalAssembledChars || 0} chars)`
                    : `◷ Running...`}
                </Typography>
              </Box>
            )}

            {/* Validation status */}
            {pd.validationStatus && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography sx={{ fontSize: "9.5px", color: C.dim, fontFamily: C.mono }}>
                  Quality Check:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "9.5px",
                    fontWeight: 600,
                    fontFamily: C.mono,
                    color:
                      pd.validationStatus === "complete"
                        ? pd.isComplete
                          ? "#10b981"
                          : C.amber
                        : C.indigo,
                  }}
                >
                  {pd.validationStatus === "complete"
                    ? pd.isComplete
                      ? "✓ Passed"
                      : "⚠ Gaps Identified"
                    : "◷ Validating..."}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Dimension Scores Breakdown */}
          {pd.dimensionScores && Object.keys(pd.dimensionScores).length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                gap: 1,
                mt: 0.5,
                pt: 1,
                borderTop: `1px dashed ${C.border}`,
              }}
            >
              {Object.entries(pd.dimensionScores).map(([dim, score]) => {
                const isPassing = score >= (pd.validationThreshold || 0.85);
                return (
                  <Box key={dim} sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "9px",
                      }}
                    >
                      <span style={{ color: C.dim, fontFamily: C.mono }}>{dim}</span>
                      <span
                        style={{
                          fontWeight: 600,
                          fontFamily: C.mono,
                          color: isPassing ? "#10b981" : C.amber,
                        }}
                      >
                        {(score * 100).toFixed(0)}%
                      </span>
                    </Box>
                    <Box sx={{ height: 3, bgcolor: C.border, borderRadius: 1, overflow: "hidden" }}>
                      <Box
                        sx={{
                          height: "100%",
                          width: `${score * 100}%`,
                          bgcolor: isPassing ? "#10b981" : C.amber,
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

/* Draggable Paper Component */
function DraggablePaper(props) {
  const nodeRef = useRef(null);
  const { isMinimized, ...paperProps } = props;

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".drag-handle"
      cancel={'[class*="MuiDialogContent-root"]'}
      defaultPosition={{ x: 0, y: 0 }}
      key={isMinimized ? "minimized" : "maximized"}
    >
      <Paper ref={nodeRef} {...paperProps} />
    </Draggable>
  );
}

/* ── Main modal ── */
const WorkflowExecutionStreamModal = ({
  _modalKey,
  open,
  isMinimized = false,
  stackIndex = 0,
  onClose,
  onToggleMinimize = () => {},
  workflowId,
  stageNumber,
  stageName = "Stage",
  missionName = "Workflow",
  stagePrompt = null,
  projectId = null, // Add project_id to props
  onComplete = null, // Callback when modal closes
}) => {
  const [confirm, confirmDialog] = useConfirm();
  const logRef = useRef(null);
  const { currentConversation, initializeConversation } = useApp(); // Get conversation from context
  const [userQuery, setUserQuery] = useState(stagePrompt || "");
  const [webVal, setWebVal] = useState(true); // Default to ENABLED
  const [reportType, setReportType] = useState("comprehensive");
  const [started, setStarted] = useState(false);
  const [internalOpen, setInternalOpen] = useState(open);
  const [notificationSent, setNotificationSent] = useState(false);
  const [autoExecutePending, setAutoExecutePending] = useState(true); // ALWAYS auto-execute

  const {
    isStreaming,
    isConnected,
    error,
    phaseState,
    activePhase,
    overallProgress,
    analysisText,
    executiveSummaryText,
    allEvents,
    startTime,
    startExecution,
    stopExecution,
    resetExecution,
    getPhaseData,
    isComplete,
    getFormattedDuration,
  } = useWorkflowStreamingExecution(workflowId, stageNumber);

  // Check if analysis is complete
  const done = isComplete();

  // Keep modal open when minimized, even if parent tries to close it
  useEffect(() => {
    if (isMinimized) {
      setInternalOpen(true);
      return;
    }
    setInternalOpen(open);
  }, [open, isMinimized, workflowId]);

  // Initialize conversation when modal opens with project_id
  useEffect(() => {
    const initConv = async () => {
      if (open && projectId && !currentConversation?.id) {
        await initializeConversation(projectId);
      }
    };
    initConv();
    // NOTE: Do NOT include currentConversation?.id in the dependency array.
    // We only want to initialize once when modal opens, not re-trigger when conversation changes.
  }, [open, projectId, initializeConversation]);

  useEffect(() => {
    // Auto-execute immediately when modal opens - use conversation from context
    if (autoExecutePending && !started && !isStreaming) {
      const conversationId = currentConversation?.id || null;
      const queryToExecute = stagePrompt || userQuery || `Execute Stage ${stageNumber}`;
      setAutoExecutePending(false);
      setStarted(true);
      startExecution(queryToExecute, webVal, conversationId, reportType);
    }
  }, [
    autoExecutePending,
    started,
    isStreaming,
    stagePrompt,
    userQuery,
    stageNumber,
    webVal,
    reportType,
    startExecution,
  ]);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [phaseState, analysisText]);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Send notification when execution completes
  useEffect(() => {
    if (done && started && !notificationSent) {
      if ("Notification" in window && Notification.permission === "granted") {
        const title = "Stage Execution Complete";
        const body = `Stage ${stageNumber} execution finished - ${getFormattedDuration()}`;

        const notification = new Notification(title, {
          body,
          icon: "/favicon.ico",
          badge: "/favicon.ico",
          tag: `execution-${workflowId}-${stageNumber}`,
          requireInteraction: false,
          silent: false,
        });

        notification.onclick = () => {
          window.focus();
          if (isMinimized) {
            onToggleMinimize();
          }
          setInternalOpen(true);
          notification.close();
        };

        // Auto-close after 10 seconds
        setTimeout(() => notification.close(), 10000);

        setNotificationSent(true);
      }
    }
  }, [
    done,
    started,
    notificationSent,
    stageNumber,
    workflowId,
    getFormattedDuration,
    isMinimized,
    onToggleMinimize,
  ]);

  const go = async () => {
    if (!userQuery.trim()) {
      return;
    }
    setStarted(true);
    setNotificationSent(false);
    await startExecution(userQuery, webVal, currentConversation?.id || null, reportType);
  };
  const reset = () => {
    resetExecution();
    setStarted(false);
    setUserQuery(stagePrompt || "");
    setWebVal(true);
    setReportType("comprehensive");
    setNotificationSent(false);
    setAutoExecutePending(true);
  };
  const close = async () => {
    if (isMinimized) {
      onToggleMinimize();
      return;
    }
    if (isStreaming) {
      const ok = await confirm({
        title: "Execution still running",
        description: "Closing now will stop the execution in progress. Close anyway?",
        confirmText: "Close and stop",
        severity: "warning",
      });
      if (!ok) {
        return;
      }
    }
    if (isStreaming) {
      stopExecution();
    }

    // Call onComplete callback before closing
    if (onComplete) {
      onComplete();
    }

    onClose();
    reset();
  };
  const exportLog = () => {
    const blob = new Blob([exportExecutionLog(phaseState, allEvents)], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), {
      href: url,
      download: `stage-${stageNumber}-log.txt`,
    });
    a.click();
    URL.revokeObjectURL(url);
  };

  const lines = buildLines(phaseState, analysisText, executiveSummaryText, getPhaseData);

  // Check if tasks were created in Phase 6 (Task Generation)
  const phase6Data = getPhaseData(6)?.data || {};
  const tasksCreated = phase6Data.parent_tasks && phase6Data.parent_tasks.length > 0;

  return (
    <Dialog
      open={internalOpen}
      onClose={(event, _reason) => {
        if (isMinimized) {
          event?.stopPropagation();
          event?.preventDefault();
          return;
        }
        close();
      }}
      maxWidth="md"
      fullWidth
      PaperComponent={DraggablePaper}
      hideBackdrop={isMinimized}
      disableEnforceFocus={true}
      disableEscapeKeyDown={isMinimized}
      disableAutoFocus={true}
      disableRestoreFocus={true}
      disableScrollLock={true}
      keepMounted={true}
      sx={{
        pointerEvents: isMinimized ? "none" : undefined,
      }}
      PaperProps={{
        isMinimized,
        sx: {
          height: isMinimized ? "auto" : "82vh",
          maxHeight: isMinimized ? "auto" : "82vh",
          width: isMinimized ? "380px" : undefined,
          position: isMinimized ? "fixed" : undefined,
          bottom: isMinimized ? `${20 + stackIndex * 120}px` : undefined,
          right: isMinimized ? "20px" : undefined,
          top: isMinimized ? "auto" : undefined,
          left: isMinimized ? "auto" : undefined,
          margin: isMinimized ? "0" : undefined,
          borderRadius: "12px",
          border: `1px solid ${C.border}`,
          boxShadow: isMinimized
            ? "0 8px 24px rgba(15,23,42,0.2), 0 2px 8px rgba(15,23,42,0.1)"
            : "0 16px 48px rgba(15,23,42,0.14), 0 2px 8px rgba(15,23,42,0.06)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: C.bg,
          cursor: isMinimized ? "default" : undefined,
          transition: "all 0.3s ease",
          pointerEvents: "auto",
          zIndex: isMinimized ? 1300 : undefined,
        },
      }}
      BackdropProps={{
        sx: {
          background: isMinimized ? "transparent" : "rgba(15,23,42,0.35)",
          backdropFilter: isMinimized ? "none" : "blur(2px)",
          pointerEvents: "none",
        },
      }}
      slotProps={{
        backdrop: {
          invisible: isMinimized,
        },
      }}
    >
      {/* ── HEADER ── */}
      <Box
        className="drag-handle"
        sx={{
          px: 2.5,
          py: 1.5,
          background: C.surface,
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
          cursor: "move",
          userSelect: "none",
        }}
      >
        {/* Drag indicator */}
        <DragIndicator sx={{ fontSize: 16, color: C.muted, mr: -1 }} />

        {/* Dots - Red: close, Yellow: minimize/maximize, Green: maximize */}
        <Box sx={{ display: "flex", gap: "5px", mr: 0.5 }}>
          {["#f85149", "#d29922", "#3fb950"].map((c, i) => (
            <Box
              key={i}
              onClick={
                i === 0
                  ? close
                  : i === 1
                    ? onToggleMinimize
                    : i === 2
                      ? onToggleMinimize
                      : undefined
              }
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: c,
                cursor: i === 0 || i === 1 || i === 2 ? "pointer" : "default",
                "&:hover": i === 0 || i === 1 || i === 2 ? { filter: "brightness(0.8)" } : {},
              }}
            />
          ))}
        </Box>

        {/* Breadcrumb */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography sx={{ fontSize: "11px", color: C.dim, fontFamily: C.mono }}>
              {missionName}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: C.dim }}>›</Typography>
            <Typography
              sx={{ fontSize: "11px", color: C.indigo, fontFamily: C.mono, fontWeight: 600 }}
            >
              stage-{stageNumber}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: C.dim }}>·</Typography>
            <Typography sx={{ fontSize: "11px", color: C.sub, fontFamily: C.mono }}>
              {stageName}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: C.dim }}>·</Typography>
            <Typography sx={{ fontSize: "11px", color: C.indigo, fontFamily: C.mono }}>
              execution
            </Typography>
          </Box>
        </Box>

        {/* Status pill */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {started && (
            <Typography sx={{ fontSize: "10px", color: C.sub, fontFamily: C.mono }}>
              {overallProgress}%{startTime ? `  ·  ${getFormattedDuration()}` : ""}
            </Typography>
          )}
          {isStreaming && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                px: 1.25,
                py: 0.35,
                borderRadius: "20px",
                background: C.indigoBg,
                border: `1px solid ${C.indigoBdr}`,
              }}
            >
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  bgcolor: C.indigo,
                  animation: "pd 1.4s ease-in-out infinite",
                  "@keyframes pd": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                }}
              />
              <Typography
                sx={{ fontSize: "10px", color: C.indigo, fontWeight: 600, fontFamily: C.mono }}
              >
                running
              </Typography>
            </Box>
          )}
          {done && !isStreaming && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                px: 1.25,
                py: 0.35,
                borderRadius: "20px",
                background: C.indigoBg,
                border: `1px solid ${C.indigoBdr}`,
              }}
            >
              <CheckCircle sx={{ fontSize: 10, color: C.indigo }} />
              <Typography
                sx={{ fontSize: "10px", color: C.indigo, fontWeight: 600, fontFamily: C.mono }}
              >
                done
              </Typography>
            </Box>
          )}
        </Box>

        {/* Window controls */}
        <IconButton
          onClick={() => {
            onToggleMinimize();
            setInternalOpen(true);
          }}
          size="small"
          sx={{ color: C.sub, p: 0.5, borderRadius: "6px", "&:hover": { bgcolor: "grey.100" } }}
          title={isMinimized ? "Maximize" : "Minimize"}
        >
          {isMinimized ? <Maximize sx={{ fontSize: 15 }} /> : <Minimize sx={{ fontSize: 15 }} />}
        </IconButton>

        <IconButton
          onClick={close}
          size="small"
          sx={{ color: C.sub, p: 0.5, borderRadius: "6px", "&:hover": { bgcolor: "grey.100" } }}
          title="Close"
        >
          <Close sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      {/* ── PROGRESS RAIL ── */}
      {started && !isMinimized && (
        <Box
          sx={{
            height: 2,
            bgcolor: C.border,
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: `${overallProgress}%`,
              bgcolor: C.indigo,
              transition: "width 0.4s ease",
              boxShadow: `0 0 8px ${C.indigoBdr}`,
            }}
          />
        </Box>
      )}

      {/* Minimized progress indicator */}
      {isMinimized && (
        <Box sx={{ px: 2.5, py: 1.5, background: C.bg }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.75 }}>
            {isStreaming && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: C.indigo,
                  flexShrink: 0,
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1, transform: "scale(1)" },
                    "50%": { opacity: 0.5, transform: "scale(0.9)" },
                  },
                }}
              />
            )}
            <Typography sx={{ fontSize: "11px", color: C.sub, fontFamily: C.mono, flex: 1 }}>
              {isStreaming
                ? `Executing... ${overallProgress}%`
                : done
                  ? "Execution complete"
                  : "Not started"}
            </Typography>
            {activePhase && (
              <Typography sx={{ fontSize: "10px", color: C.dim, fontFamily: C.mono }}>
                Phase {activePhase}/6
              </Typography>
            )}
          </Box>

          {/* Mini progress bar */}
          <Box sx={{ height: 3, bgcolor: C.border, borderRadius: 2, overflow: "hidden" }}>
            <Box
              sx={{
                height: "100%",
                width: `${overallProgress}%`,
                bgcolor: C.indigo,
                transition: "width 0.4s ease",
              }}
            />
          </Box>

          {/* Quick stats */}
          {done && (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                gap: 1.5,
                fontSize: "10px",
                color: C.dim,
                fontFamily: C.mono,
              }}
            >
              <span>Duration: {getFormattedDuration()}</span>
              <span>·</span>
              <span style={{ color: C.success }}>Complete</span>
            </Box>
          )}
        </Box>
      )}

      {/* ── BODY ── */}
      {!isMinimized && (
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* ─ Pre-start input (only shown if somehow execution hasn't started) ─ */}
          {!started && (
            <Box
              sx={{
                flex: 1,
                p: 3,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                overflowY: "auto",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    background: C.indigoBg,
                    border: `1px solid ${C.indigoBdr}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PlayArrow sx={{ fontSize: 16, color: C.indigo }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>
                    Configure Execution
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: C.sub }}>
                    Query will be decomposed and streamed across 6 phases
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: C.sub,
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                    mb: 0.75,
                  }}
                >
                  Stage Query
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={7}
                  placeholder="Describe what to analyze or validate..."
                  value={userQuery}
                  onChange={(e) => setUserQuery(e.target.value)}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: "12.5px",
                      color: C.text,
                      fontFamily: C.mono,
                      background: C.surface,
                      "& fieldset": { borderColor: C.border },
                      "&:hover fieldset": { borderColor: C.indigoBdr },
                      "&.Mui-focused fieldset": { borderColor: C.indigo, borderWidth: "1.5px" },
                    },
                    "& .MuiInputBase-input::placeholder": { color: C.dim, opacity: 1 },
                  }}
                />
              </Box>

              {/* Report type selector */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1.25,
                  borderRadius: "8px",
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                }}
              >
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: C.text, minWidth: 80 }}>
                  Report Type
                </Typography>
                <Box sx={{ display: "flex", gap: 0.75, flex: 1 }}>
                  {[
                    { value: "standard", label: "Standard", hint: "40-50 pages" },
                    { value: "comprehensive", label: "Comprehensive", hint: "100-150 pages" },
                  ].map(({ value, label, hint }) => (
                    <Box
                      key={value}
                      onClick={() => setReportType(value)}
                      sx={{
                        flex: 1,
                        textAlign: "center",
                        py: 0.6,
                        px: 0.5,
                        borderRadius: "6px",
                        cursor: "pointer",
                        border: `1.5px solid ${reportType === value ? C.indigo : C.border}`,
                        background: reportType === value ? `${C.indigo}18` : "transparent",
                        transition: "all 0.15s",
                        "&:hover": { borderColor: C.indigo },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          color: reportType === value ? C.indigo : C.text,
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: "9.5px", color: C.sub }}>{hint}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Web validation */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderRadius: "8px",
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                }}
              >
                <VerifiedUser
                  sx={{ fontSize: 16, color: webVal ? C.indigo : C.dim, transition: "color 0.2s" }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: C.text }}>
                    Web Validation
                  </Typography>
                  <Typography sx={{ fontSize: "10.5px", color: C.sub }}>
                    Cross-reference with authoritative sources (slower)
                  </Typography>
                </Box>
                <Box
                  onClick={() => setWebVal((v) => !v)}
                  sx={{
                    width: 36,
                    height: 20,
                    borderRadius: "10px",
                    flexShrink: 0,
                    cursor: "pointer",
                    background: webVal ? C.indigo : C.border,
                    position: "relative",
                    transition: "background 0.2s",
                  }}
                >
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      bgcolor: "#fff",
                      position: "absolute",
                      top: 3,
                      left: webVal ? 19 : 3,
                      transition: "left 0.2s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* ─ Split: phase timeline + log ─ */}
          {started && (
            <>
              {/* Phase timeline sidebar */}
              <Box
                sx={{
                  width: 148,
                  flexShrink: 0,
                  borderRight: `1px solid ${C.border}`,
                  background: C.surface,
                  px: 2,
                  pt: 2,
                  pb: 1.5,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "9px",
                    fontWeight: 700,
                    color: C.dim,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    mb: 1.5,
                  }}
                >
                  Phases
                </Typography>
                {Object.keys(PHASES).map((sid, idx, arr) => (
                  <PhaseItem
                    key={sid}
                    id={parseFloat(sid)}
                    phaseData={phaseState?.[parseFloat(sid)]}
                    isActive={activePhase === parseFloat(sid)}
                    isLast={idx === arr.length - 1}
                  />
                ))}

                {/* Duration */}
                {startTime && (
                  <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${C.border}` }}>
                    <Typography sx={{ fontSize: "9px", color: C.dim, fontFamily: C.mono }}>
                      elapsed
                    </Typography>
                    <Typography
                      sx={{ fontSize: "11px", color: C.sub, fontFamily: C.mono, fontWeight: 600 }}
                    >
                      {getFormattedDuration()}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Terminal / Dashboard Container */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  background: C.bg,
                }}
              >
                <AgentExecutionDashboard
                  pd={getPhaseData(4)?.data || {}}
                  isStreaming={isStreaming}
                />

                {/* Terminal log */}
                <Box
                  ref={logRef}
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    background: C.bg,
                    px: 2.5,
                    py: 2,
                    "&::-webkit-scrollbar": { width: 4 },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": { background: C.indigoBdr, borderRadius: 2 },
                  }}
                >
                  {/* Prompt */}
                  {userQuery && (
                    <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: `1px solid ${C.border}` }}>
                      <Typography
                        sx={{ fontSize: "10px", color: C.dim, fontFamily: C.mono, mb: 0.3 }}
                      >
                        $ certbuddy execute stage-{stageNumber}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: C.sub,
                          fontFamily: C.mono,
                          pl: 1,
                          borderLeft: `2px solid ${C.border}`,
                        }}
                      >
                        {userQuery.length > 100 ? userQuery.substring(0, 100) + "…" : userQuery}
                      </Typography>
                    </Box>
                  )}

                  {/* Status */}
                  {isStreaming && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          bgcolor: C.indigo,
                          boxShadow: `0 0 5px ${C.indigoBdr}`,
                        }}
                      />
                      <Typography sx={{ fontSize: "10.5px", color: C.indigo, fontFamily: C.mono }}>
                        {isConnected ? "streaming" : "connecting…"}
                        {activePhase ? `  ·  phase ${activePhase}` : ""}
                      </Typography>
                    </Box>
                  )}

                  {/* Error */}
                  {error && (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: "6px",
                        mb: 1.5,
                        background: C.errBg,
                        border: `1px solid rgba(220,38,38,0.15)`,
                      }}
                    >
                      <Typography sx={{ fontSize: "11px", color: C.error, fontFamily: C.mono }}>
                        ✗ {error}
                      </Typography>
                    </Box>
                  )}

                  {/* Lines */}
                  {lines.map((item, i) => (
                    <Line
                      key={item.id}
                      item={item}
                      isLast={i === lines.length - 1}
                      streaming={isStreaming}
                    />
                  ))}

                  {isStreaming && lines.length === 0 && (
                    <Typography sx={{ fontSize: "11px", color: C.dim, fontFamily: C.mono }}>
                      initializing…
                    </Typography>
                  )}

                  {/* Done */}
                  {done && (
                    <Box
                      sx={{
                        mt: 2,
                        pt: 1.5,
                        borderTop: `1px solid ${C.indigoBdr}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 13, color: C.indigo }} />
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: C.indigo,
                          fontFamily: C.mono,
                          fontWeight: 600,
                        }}
                      >
                        complete · {getFormattedDuration()}
                      </Typography>
                    </Box>
                  )}

                  {/* Cursor */}
                  {isStreaming && (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 12,
                          bgcolor: C.indigo,
                          opacity: 0.5,
                          animation: "bl 1s step-end infinite",
                          "@keyframes bl": { "0%,100%": { opacity: 0.5 }, "50%": { opacity: 0 } },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ── FOOTER ── */}
      {!isMinimized && (
        <Box
          sx={{
            px: 2.5,
            py: 1.25,
            borderTop: `1px solid ${C.border}`,
            background: C.surface,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          {started && (
            <>
              <Button
                startIcon={<Download sx={{ fontSize: 13 }} />}
                onClick={exportLog}
                size="small"
                sx={btn()}
              >
                Export Log
              </Button>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          {started && !isStreaming && !tasksCreated && (
            <Button
              startIcon={<Refresh sx={{ fontSize: 13 }} />}
              onClick={reset}
              size="small"
              sx={btn()}
            >
              Reset
            </Button>
          )}
          {isStreaming && (
            <Button
              startIcon={<Stop sx={{ fontSize: 13 }} />}
              onClick={stopExecution}
              size="small"
              sx={btn({ color: C.error, border: "rgba(220,38,38,0.25)", hoverBg: C.errBg })}
            >
              Stop
            </Button>
          )}
          <Button onClick={close} size="small" sx={btn()}>
            Close
          </Button>
          {!started && (
            <Button
              startIcon={<PlayArrow sx={{ fontSize: 13 }} />}
              onClick={go}
              disabled={!userQuery.trim()}
              size="small"
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 700,
                color: "#fff",
                borderRadius: "7px",
                px: 2,
                py: 0.7,
                background: `linear-gradient(135deg, #818cf8, ${C.indigo})`,
                boxShadow: "0 2px 8px rgba(86,100,210,0.3)",
                "&:hover": { boxShadow: "0 4px 14px rgba(86,100,210,0.45)" },
                "&:disabled": { background: "#e2e8f0", color: C.dim, boxShadow: "none" },
              }}
            >
              Run
            </Button>
          )}
        </Box>
      )}
      {confirmDialog}
    </Dialog>
  );
};

function btn(opts = {}) {
  const { color = "#64748b", border = "#e2e8f0", hoverBg = "#f1f5f9" } = opts;
  return {
    textTransform: "none",
    fontSize: "11.5px",
    fontWeight: 600,
    color,
    border: `1px solid ${border}`,
    borderRadius: "6px",
    px: 1.5,
    py: 0.6,
    "&:hover": { bgcolor: hoverBg },
  };
}

export default WorkflowExecutionStreamModal;
